import axios, { AxiosRequestConfig, Method } from 'axios';
import { environmentConfig } from '../config';
import {
  Environment,
  ENVIRONMENT_HEADER,
  METHOD_OVERRIDE_HEADER,
  METHOD_OVERRIDE_QUERY_PARAM,
  TUNNELLED_METHODS,
} from '@mapp-byte-connect/config';

type RequestParams = Record<string, unknown> | unknown;
type RequestHeaders = Record<string, string>;
const BRAND_HEADER = 'x-brand-id';
const DEFAULT_TIMEOUT_MS = 20_000;

/**
 * Send PATCH / PUT / DELETE as POST + `X-HTTP-Method-Override` **and** `?_method=`.
 * The Akamai edge in front of the deployed app rejects those methods with a 501
 * before they reach the API; the API's `methodOverride` middleware restores the
 * intended method from either signal. Both are sent because CloudFront forwards
 * only an allow-list of headers to the API but always forwards the query string.
 * Always on so local and deployed environments share one path.
 */
const TUNNEL_METHODS_VIA_POST = true;
const TUNNELLED = new Set<string>(TUNNELLED_METHODS);

class NetworkManager {
  private static instance: NetworkManager;
  private authToken: string | null = null;
  private environment: Environment = 'local';
  private brandId: string | null = null;
  private onUnauthorizedCallback: (() => void) | null = null;

  static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }

    return NetworkManager.instance;
  }

  setAuthorizationToken(token: string) {
    this.authToken = token;
  }

  clearAuthorizationToken() {
    this.authToken = null;
  }

  setEnvironment(environment: Environment) {
    this.environment = environment;
  }

  setBrandId(brandId: string | null) {
    this.brandId = brandId;
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  getBaseUrl(): string {
    return environmentConfig.API_BASE_URL;
  }

  getHeaders(): Record<string, string> {
    return {
      ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      [ENVIRONMENT_HEADER]: this.environment,
      ...(this.brandId ? { [BRAND_HEADER]: this.brandId } : {}),
    };
  }

  // Set callback to be called when a 401 Unauthorized response is received
  setOnUnauthorized(callback: (() => void) | null) {
    this.onUnauthorizedCallback = callback;
  }

  async requestHandler(
    method: Method,
    path: string,
    params?: RequestParams,
    customHeaders?: RequestHeaders,
    timeoutMs?: number
  ) {
    const headers: RequestHeaders = {
      ...(this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      [ENVIRONMENT_HEADER]: this.environment,
      ...(this.brandId ? { [BRAND_HEADER]: this.brandId } : {}),
      ...({
        'Content-Type': 'application/json',
      } as RequestHeaders),
      ...(customHeaders || {}),
    };

    if (!navigator.onLine) {
      throw new axios.AxiosError('No network connection', 'ERR_NETWORK');
    }

    const effectiveTimeout = timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const abortController = effectiveTimeout > 0 ? new AbortController() : undefined;

    const upper = method.toUpperCase();
    const tunnel = TUNNEL_METHODS_VIA_POST && TUNNELLED.has(upper);
    let url = `${environmentConfig.API_BASE_URL}${path}`;
    if (tunnel) {
      headers[METHOD_OVERRIDE_HEADER] = upper;
      url += `${url.includes('?') ? '&' : '?'}${METHOD_OVERRIDE_QUERY_PARAM}=${upper}`;
    }

    const config: AxiosRequestConfig = {
      method: tunnel ? 'post' : method,
      url,
      headers,
      ...(abortController ? { signal: abortController.signal } : {}),
    };

    if (upper === 'GET') {
      config.params = params;
    } else {
      // Always send a JSON body on tunnelled requests: the edge answers a body-less POST with 411
      config.data = params ?? (tunnel ? {} : undefined);
    }

    try {
      const response = abortController
        ? await this.raceTimeout(axios(config), effectiveTimeout, abortController)
        : await axios(config);
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.error('API error:', error.response?.data || error.message);

        // Handle 401 Unauthorized - clear auth state and redirect to login
        if (error.response?.status === 401 && this.onUnauthorizedCallback) {
          this.onUnauthorizedCallback();
        }
      } else {
        console.error('API error:', error);
      }
      throw error;
    }
  }

  /**
   * Race an axios promise against a hard timer.  If the timer wins it aborts
   * the underlying request and rejects with an AxiosError (code ECONNABORTED)
   * so callers can detect the timeout the same way as a native axios timeout.
   */
  private raceTimeout<T>(request: Promise<T>, ms: number, controller: AbortController): Promise<T> {
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new axios.AxiosError(`timeout of ${ms}ms exceeded`, 'ECONNABORTED'));
      }, ms);
    });
    return Promise.race([request, timeout]).finally(() => clearTimeout(timer!));
  }

  async get<T>(
    path: string,
    params?: Record<string, unknown>,
    headers?: RequestHeaders
  ): Promise<T> {
    return this.requestHandler('get', path, params, headers);
  }

  async post<T>(
    path: string,
    params?: Record<string, unknown> | unknown,
    headers?: RequestHeaders,
    /** When set, aborts the request after this many ms (fail-fast). */
    timeoutMs?: number
  ): Promise<T> {
    return this.requestHandler('post', path, params, headers, timeoutMs);
  }

  async put<T>(
    path: string,
    params?: Record<string, unknown>,
    headers?: RequestHeaders
  ): Promise<T> {
    return this.requestHandler('put', path, params, headers);
  }

  async patch<T>(
    path: string,
    params?: Record<string, unknown>,
    headers?: RequestHeaders
  ): Promise<T> {
    return this.requestHandler('patch', path, params, headers);
  }

  async delete<T>(
    path: string,
    params?: Record<string, unknown> | unknown,
    headers?: RequestHeaders
  ): Promise<T> {
    return this.requestHandler('delete', path, params, headers);
  }

  /**
   * GET request that returns a Blob (e.g. for file download)
   */
  async getBlob(path: string, params?: Record<string, string>, timeoutMs?: number): Promise<Blob> {
    if (!navigator.onLine) {
      throw new axios.AxiosError('No network connection', 'ERR_NETWORK');
    }
    let url = `${environmentConfig.API_BASE_URL}${path}`;
    if (params && Object.keys(params).length > 0) {
      const search = new URLSearchParams(params).toString();
      url += (url.includes('?') ? '&' : '?') + search;
    }
    const effectiveTimeout = timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const controller = effectiveTimeout > 0 ? new AbortController() : undefined;
    try {
      const request = axios.get(url, {
        headers: this.getHeaders(),
        responseType: 'blob',
        ...(controller ? { signal: controller.signal } : {}),
      });
      const response = controller
        ? await this.raceTimeout(request, effectiveTimeout, controller)
        : await request;
      return response.data as Blob;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          throw new Error(parsed.message || parsed.error || 'Download failed');
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message !== 'Download failed') {
            throw new Error(`Export failed (HTTP ${error.response.status})`);
          }
          throw parseErr;
        }
      }
      throw error;
    }
  }

  /**
   * POST FormData (e.g. for file upload). Omits Content-Type so axios sets multipart boundary.
   */
  async postFormData<T>(path: string, formData: FormData, timeoutMs?: number): Promise<T> {
    if (!navigator.onLine) {
      throw new axios.AxiosError('No network connection', 'ERR_NETWORK');
    }
    const headers: RequestHeaders = {
      ...this.getHeaders(),
    };
    delete (headers as Record<string, unknown>)['Content-Type'];
    const effectiveTimeout = timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const controller = effectiveTimeout > 0 ? new AbortController() : undefined;
    const request = axios.post(`${environmentConfig.API_BASE_URL}${path}`, formData, {
      headers,
      ...(controller ? { signal: controller.signal } : {}),
    });
    const response = controller
      ? await this.raceTimeout(request, effectiveTimeout, controller)
      : await request;
    return response.data as T;
  }
}

export default NetworkManager.getInstance();
